// Central registry of Cloudinary Named Transformations used across D2C adapters.
//
// Focal point: when hasFocalPoint is set on a MediaImage, withFocalPoint() chains
// g_custom as a separate URL segment after the Named Transform:
//   t_d2c_widescreen/g_custom,w_1920  (focal point)
//   t_d2c_widescreen/w_1920           (AI gravity via g_auto in Named Transform)
// No separate _focal Named Transforms needed in Cloudinary.
//
// Named Transformation definitions (Cloudinary — g_auto for AI gravity):
//   CLOUDINARY_TRANSFORMATION_D2C_SQUARE      → t_d2c_square     (ar_1:1,  c_fill, g_auto)
//   CLOUDINARY_TRANSFORMATION_D2C_PORTRAIT    → t_d2c_portrait   (ar_3:4,  c_fill, g_auto)
//   CLOUDINARY_TRANSFORMATION_D2C_WIDESCREEN  → t_d2c_widescreen (ar_16:9, c_fill, g_auto)
//   CLOUDINARY_TRANSFORMATION_D2C_ULTRAWIDE   → t_d2c_ultrawide  (ar_21:9, c_fill, g_auto)
//   CLOUDINARY_TRANSFORMATION_D2C_TALL        → t_d2c_tall       (ar_1:2,  c_fill, g_auto)
//   CLOUDINARY_TRANSFORMATION_D2C_AUTO        → (no named transform — delivery only)
//
// f_auto and q_auto are NOT included here — insertCloudinaryTransform appends
// them automatically as separate URL segments.

// reserved for delivery-only (no crop): Named Transformation migration placeholder
export const CLOUDINARY_TRANSFORMATION_D2C_AUTO: string[] = [];

// used by: SectionTextImage (main image), SectionGallery (simple-columns), SectionQuote (cite avatar)
export const CLOUDINARY_TRANSFORMATION_D2C_SQUARE = "t_d2c_square";

// used by: SectionGallery (row, staggered)
export const CLOUDINARY_TRANSFORMATION_D2C_PORTRAIT = "t_d2c_portrait";

// used by: SectionMedia (desktop 16:9 + all mobile), SectionHero (grid-16-9 desktop)
export const CLOUDINARY_TRANSFORMATION_D2C_WIDESCREEN = "t_d2c_widescreen";

// used by: SectionMedia (desktop 21:9), SectionHero (grid-21-9 desktop)
export const CLOUDINARY_TRANSFORMATION_D2C_ULTRAWIDE = "t_d2c_ultrawide";

// used by: SectionHero (grid-16-9 + grid-21-9 mobile)
export const CLOUDINARY_TRANSFORMATION_D2C_TALL = "t_d2c_tall";

// Resolves the final Cloudinary transform to use. When focal is true, g_custom is
// added so Cloudinary uses the focal point set on the image in the Media Library:
// - Named Transform (string): returns [transform, "g_custom"] so insertCloudinaryTransform
//   chains them as separate URL segments → t_d2c_tall,w_1536/g_custom
//   (Cloudinary rejects t_name,g_custom in the same segment)
// - Inline transform (string[]): replaces g_auto with g_custom in place.
// When focal is false the transform is returned unchanged.
export function resolveTransform(
  transform: string | string[],
  focal = false
): string | string[] {
  if (!focal) {
    return transform;
  }

  if (Array.isArray(transform)) {
    return transform.map((t) => (t === "g_auto" ? "g_custom" : t));
  }

  return [transform, "g_custom"];
}
